import { useState, type FC } from 'react';
import type { ThreeColumnProps as Props } from './types';
import { withChildren } from '@builder.io/react';
import { cls } from '@/utils/cls';
import style from './ThreeColumn.module.scss';
import RichText from '../RichText';
import { useColorProvider } from '@/utils/ColorProvider';
import Image from 'next/image';
import { hasNonEmptyRichTextField } from '@/utils/richText';
const ICON_THRESHOLD = 150;
const DEFAULT_WIDTH = 600;
const DEFAULT_HEIGHT = 400;
interface ColumnImageProps {
  imageUrl: string;
}
const ColumnImage: FC<ColumnImageProps> = ({
  imageUrl
}) => {
  const [isIcon, setIsIcon] = useState(false);
  const handleLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const img = event.currentTarget;
    const iconDetected = img.naturalWidth < ICON_THRESHOLD || img.naturalHeight < ICON_THRESHOLD;
    setIsIcon(iconDetected);
  };
  return <div className={style.imageContainer} data-sentry-component="ColumnImage" data-sentry-source-file="ThreeColumn.tsx">
			<Image src={imageUrl} alt="" role="presentation" width={DEFAULT_WIDTH} height={DEFAULT_HEIGHT} className={cls(style.image, isIcon && style.icon)} sizes="(max-width: 768px) 100vw, 33vw" onLoad={handleLoad} data-sentry-element="Image" data-sentry-source-file="ThreeColumn.tsx" />
		</div>;
};
const ThreeColumn: FC<Props> = ({
  extendStyles,
  heading,
  copy,
  cardStyling,
  alignCenter,
  columns,
  children,
  backgroundColor,
  lockImageSize,
  circularImages,
  lowerCopy
}) => {
  const {
    colors
  } = useColorProvider();
  const _className = cls(style.root, extendStyles, (!!cardStyling || !!alignCenter) && style.center);
  const colStyles = cls(style.wrapper, !!cardStyling && style.cards);
  return <section className={_className} style={{
    backgroundColor
  }} data-sentry-component="ThreeColumn" data-sentry-source-file="ThreeColumn.tsx">
			<div className={style.content}>
				{hasNonEmptyRichTextField(heading) && <RichText text={heading} />}
				{hasNonEmptyRichTextField(copy) && <RichText text={copy} />}
				<div className={colStyles} style={{
        borderColor: colors.secondaryAccentColor || 'transparent',
        marginBottom: 0
      }} data-testid="three-column">
					{columns.map((col, i) => {
          const paddingClass = cls(style.column, !!col.image ? '' : style.altPadding, lockImageSize && style.lockImage, circularImages && style.circularImage);
          return <div key={`column-${i}`} className={paddingClass} style={{
            backgroundColor: cardStyling ? 'white' : backgroundColor
          }}>
								{col.image && <ColumnImage imageUrl={col.image} />}
								<div className={style.columnContent}>
									{hasNonEmptyRichTextField(col.columnHeading) && <RichText text={col.columnHeading} className={col.horizontalRule ? style.hr : ''} style={{
                borderColor: col.hrColor
              }} />}
									{hasNonEmptyRichTextField(col.columnCopy) && <RichText text={col.columnCopy} />}
								</div>
							</div>;
        })}
				</div>
				{hasNonEmptyRichTextField(lowerCopy) && <RichText text={lowerCopy} />}
				{children}
			</div>
		</section>;
};
export default withChildren(ThreeColumn);